import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { NumberInput } from '../components/NumberInput';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Save, Plus, Trash2, Send, Check, X, Clock, FileText, Edit, AlertTriangle, Eye, Download, Printer, Mail, PenLine, UserX, CheckCircle2 } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import api from '../utils/api';
import { formatCurrency, formatDateTime, getStatusLabel, getStatusColor } from '../utils/helpers';
import { toast } from 'sonner';
import { useConfirm } from '../context/ConfirmContext';
import { SignatureDialog } from '../components/SignatureDialog';
import { ItemCombobox } from '../components/ItemCombobox';
import { FreeArticleDialog } from '../components/FreeArticleDialog';
import { PromoteToCatalogDialog } from '../components/PromoteToCatalogDialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

export const WorkOrderDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  const confirm = useConfirm();
  const isNew = id === 'new';
  const isEmployee = user?.role === 'EMPLOYEE';
  
  const [workOrder, setWorkOrder] = useState({
    customer_id: '',
    site_id: '__none__',
    notes: '',
    work_date: new Date().toISOString().split('T')[0]
  });
  
  const [lines, setLines] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [items, setItems] = useState([]);
  const [customerPrices, setCustomerPrices] = useState([]);
  const [sitePrices, setSitePrices] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(isNew); // Start in edit mode only for new work orders
  const [initialLoadDone, setInitialLoadDone] = useState(false); // Prevent useEffect from overwriting prices on load

  // Share dialog state
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareContacts, setShareContacts] = useState([]);
  const [selectedRecipient, setSelectedRecipient] = useState('');
  const [shareEmail, setShareEmail] = useState('');
  const [sendingWoEmail, setSendingWoEmail] = useState(false);

  // Signature dialog state
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);

  // ----- Original vs Revised view (post-submit) -----
  // Toggle between the operator-frozen "Originale" snapshot and the live "Revisione".
  const [viewMode, setViewMode] = useState('revised'); // 'revised' | 'original'
  const [originalSnapshot, setOriginalSnapshot] = useState(null);
  const [auditEvents, setAuditEvents] = useState([]);
  // Confirm dialog state for line deletion during review
  const [deleteLineIndex, setDeleteLineIndex] = useState(null);

  // Free-article picker state (combobox -> popup)
  const [freeDialogOpen, setFreeDialogOpen] = useState(false);
  const [freeDialogTargetIndex, setFreeDialogTargetIndex] = useState(null);
  const [freeDialogInitialName, setFreeDialogInitialName] = useState('');

  // Promote-to-catalog dialog state (reviewer only)
  const [promoteLineIndex, setPromoteLineIndex] = useState(null);

  // Approve confirmation when free articles still lack price/IVA
  const [approveWarnings, setApproveWarnings] = useState(null); // null or array of strings

  // Reset edit mode when navigating to a different work order
  useEffect(() => {
    setIsEditMode(isNew);
    setInitialLoadDone(false);
    setViewMode('revised');
    setOriginalSnapshot(null);
    setAuditEvents([]);
  }, [id, isNew]);

  useEffect(() => {
    const initLoad = async () => {
      loadCustomers();
      const loadedItems = await loadItems();
      if (!isNew) {
        await loadWorkOrder(loadedItems);
      }
      setInitialLoadDone(true);
    };
    initLoad();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load customer prices and sites when customer changes (only after initial load)
  useEffect(() => {
    if (workOrder.customer_id) {
      loadSites(workOrder.customer_id);
      loadCustomerPrices(workOrder.customer_id).then(custPrices => {
        if (initialLoadDone && !isEmployee && lines.length > 0) {
          const updatedLines = updateLinePrices(lines, custPrices, sitePrices);
          setLines(updatedLines);
        }
      });
    }
  }, [workOrder.customer_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load site prices when site changes (only after initial load)
  useEffect(() => {
    const siteId = workOrder.site_id;
    if (siteId && siteId !== '__none__') {
      loadSitePrices(siteId).then(sPrices => {
        if (initialLoadDone && !isEmployee && lines.length > 0) {
          const updatedLines = updateLinePrices(lines, customerPrices, sPrices);
          setLines(updatedLines);
        }
      });
    } else {
      setSitePrices([]);
      if (initialLoadDone && !isEmployee && lines.length > 0) {
        const updatedLines = updateLinePrices(lines, customerPrices, []);
        setLines(updatedLines);
      }
    }
  }, [workOrder.site_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCustomers = async () => {
    try {
      const response = await api.getCustomers();
      setCustomers(response.data);
    } catch (error) {
      console.error('Failed to load customers:', error);
    }
  };

  const loadSites = async (customerId) => {
    try {
      const response = await api.getCustomerSites(customerId);
      setSites(response.data);
    } catch (error) {
      console.error('Failed to load sites:', error);
    }
  };

  const loadItems = async () => {
    try {
      const response = await api.getItems(true);
      setItems(response.data);
      return response.data; // Return items for chaining
    } catch (error) {
      console.error('Failed to load items:', error);
      return [];
    }
  };

  // Load customer-specific prices
  const loadCustomerPrices = async (customerId) => {
    try {
      const response = await api.getCustomerPrices(customerId);
      setCustomerPrices(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to load customer prices:', error);
      setCustomerPrices([]);
      return [];
    }
  };

  // Load site-specific prices
  const loadSitePrices = async (siteId) => {
    if (!siteId || siteId === '__none__') {
      setSitePrices([]);
      return [];
    }
    try {
      const response = await api.getSitePrices(siteId);
      setSitePrices(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to load site prices:', error);
      setSitePrices([]);
      return [];
    }
  };

  // Get price for an item using cascade: Site > Customer > Standard
  const getPriceForItem = (itemId, itemsList = items, custPrices = customerPrices, sPrices = sitePrices) => {
    const sitePrice = sPrices.find(p => p.item_id === itemId);
    if (sitePrice?.unit_price_chf) {
      return { price: sitePrice.unit_price_chf, source: 'site' };
    }
    const customerPrice = custPrices.find(p => p.item_id === itemId);
    if (customerPrice?.unit_price_chf) {
      return { price: customerPrice.unit_price_chf, source: 'customer' };
    }
    const item = itemsList.find(i => i.id === itemId);
    return { price: item?.standard_price_chf || 0, source: 'standard' };
  };

  // Update all line prices based on current site/customer selection
  const updateLinePrices = (newLines, custPrices = customerPrices, sPrices = sitePrices) => {
    return newLines.map(line => {
      if (!line.item_id) return line;
      const { price } = getPriceForItem(line.item_id, items, custPrices, sPrices);
      const discount = line.discount_percent || 0;
      const netAmount = price * line.quantity * (1 - discount / 100);
      return {
        ...line,
        assigned_unit_price_chf: price,
        net_amount: netAmount
      };
    });
  };

  const loadWorkOrder = async (loadedItems = []) => {
    try {
      const response = await api.getWorkOrder(id);
      setWorkOrder(response.data);
      
      // Load customer and site prices
      let custPrices = [];
      let sPrices = [];
      if (response.data.customer_id) {
        custPrices = await loadCustomerPrices(response.data.customer_id);
      }
      if (response.data.site_id && response.data.site_id !== '__none__') {
        sPrices = await loadSitePrices(response.data.site_id);
      }

      // Enrich lines with item data and cascaded prices
      const loadedLines = response.data.lines || [];
      const itemsList = loadedItems.length > 0 ? loadedItems : items;
      const enrichedLines = loadedLines.map(line => {
        const item = itemsList.find(i => i.id === line.item_id);
        // Use existing assigned price if available, otherwise calculate from cascade
        let assignedPrice = line.assigned_unit_price_chf;
        if (!assignedPrice && !isEmployee) {
          const { price } = getPriceForItem(line.item_id, itemsList, custPrices, sPrices);
          assignedPrice = price;
        }
        const discount = line.discount_percent || 0;
        const netAmount = line.net_amount || (assignedPrice ? assignedPrice * line.quantity * (1 - discount / 100) : 0);
        return {
          ...line,
          unit: item?.unit || line.unit || '',
          standard_price_chf: item?.standard_price_chf || line.standard_price_chf || 0,
          assigned_unit_price_chf: assignedPrice,
          net_amount: netAmount
        };
      });
      setLines(enrichedLines);

      // Load audit timeline & original snapshot (post-submit only)
      const status = response.data.status;
      if (status && status !== 'DRAFT') {
        try {
          const auditResp = await api.getWorkOrderAudit(id);
          setAuditEvents(auditResp.data || []);
        } catch {
          setAuditEvents([]);
        }
        if (response.data.has_original_snapshot) {
          try {
            const origResp = await api.getWorkOrderOriginal(id);
            const snap = origResp.data || null;
            if (snap && snap.lines && Array.isArray(snap.lines)) {
              // Enrich snapshot lines with display unit so the read-only view shows
              // the same column as the live "Revisione" view.
              const itemsList = loadedItems.length > 0 ? loadedItems : items;
              snap.lines = snap.lines.map(ln => {
                const it = itemsList.find(i => i.id === ln.item_id);
                return {
                  ...ln,
                  unit: it?.unit || ln.item_unit || ln.unit || '',
                };
              });
            }
            setOriginalSnapshot(snap);
            // Employees must always see the frozen original — never the post-review edits.
            if (isEmployee && snap) {
              setViewMode('original');
            }
          } catch {
            setOriginalSnapshot(null);
          }
        } else {
          setOriginalSnapshot(null);
        }
      } else {
        setAuditEvents([]);
        setOriginalSnapshot(null);
      }
    } catch (error) {
      toast.error('Errore nel caricamento bollettino');
      navigate('/app/work-orders');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!workOrder.customer_id) {
      toast.error('Seleziona un cliente');
      return;
    }

    if (lines.length === 0) {
      toast.error('Aggiungi almeno un articolo');
      return;
    }

    // Block save when any free article still lacks its unit. The unit must be
    // chosen at creation time so the rest of the flow (PDF, invoice) has a
    // valid descriptor for the article.
    const missingUnit = lines.filter(
      (l) => l.is_free_article && !(l.item_unit || l.unit)
    );
    if (missingUnit.length > 0) {
      const names = missingUnit.map(l => `“${l.item_name || '—'}”`).join(', ');
      toast.error(`Imposta l'unità di misura per: ${names}`);
      return;
    }

    setSaving(true);

    try {
      let workOrderId = id;
      const siteIdToSave = workOrder.site_id === '__none__' ? null : workOrder.site_id;

      // Helper: payload to send for a line on POST /lines.
      // Free articles travel with item_id=null and their snapshot (name/unit/MWST).
      const addLinePayload = (line) => (line.is_free_article ? {
        item_id: null,
        item_name: line.item_name,
        item_unit: line.item_unit || line.unit || '',
        mwst_rate_snapshot: line.mwst_rate_snapshot ?? null,
        is_free_article: true,
        quantity: line.quantity,
        date: workOrder.work_date,
        line_notes: line.line_notes || '',
        discount_percent: line.discount_percent || 0,
      } : {
        item_id: line.item_id,
        quantity: line.quantity,
        date: workOrder.work_date,
        line_notes: line.line_notes || '',
      });

      // Helper: payload for PUT /lines/{id}. Free articles preserve their snapshot fields.
      const updateLinePayload = (line) => (line.is_free_article ? {
        quantity: line.quantity,
        date: workOrder.work_date,
        line_notes: line.line_notes || '',
        item_name: line.item_name,
        item_unit: line.item_unit || line.unit || '',
        mwst_rate_snapshot: line.mwst_rate_snapshot ?? null,
      } : {
        quantity: line.quantity,
        date: workOrder.work_date,
        line_notes: line.line_notes || '',
      });

      // A line is "saveable" if it points to a catalog item OR is a complete free article.
      const isSaveable = (line) => !!line.item_id || (line.is_free_article && line.item_name);

      if (isNew) {
        // Create work order
        const woResponse = await api.createWorkOrder({
          customer_id: workOrder.customer_id,
          site_id: siteIdToSave,
          notes: workOrder.notes,
          work_date: workOrder.work_date
        });
        workOrderId = woResponse.data.id;

        for (const line of lines) {
          if (isSaveable(line)) {
            await api.addWorkOrderLine(workOrderId, addLinePayload(line));
          }
        }

        toast.success('Bollettino creato con successo');
        navigate(`/app/work-orders/${workOrderId}`);
      } else {
        // Update work order (customer, site, notes, work_date)
        await api.updateWorkOrder(workOrderId, {
          customer_id: workOrder.customer_id,
          site_id: siteIdToSave,
          notes: workOrder.notes,
          work_date: workOrder.work_date
        });

        for (const line of lines) {
          if (line.id && isSaveable(line)) {
            await api.updateWorkOrderLine(workOrderId, line.id, updateLinePayload(line));
          } else if (!line.id && isSaveable(line)) {
            await api.addWorkOrderLine(workOrderId, addLinePayload(line));
          }
        }

        toast.success('Bollettino aggiornato');
        setIsEditMode(false); // Exit edit mode after saving
        loadWorkOrder(); // Reload to get fresh data
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSignature = async ({ signature_base64, signer_name }) => {
    try {
      await api.saveWorkOrderSignature(workOrder.id, {
        signature_base64,
        signer_name,
        customer_not_present: false,
        device: 'desktop',
      });
      toast.success('Firma salvata');
      loadWorkOrder();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvare la firma');
    }
  };

  const handleSubmit = async () => {
    if (!workOrder.id) {
      toast.error('Salva prima il bollettino');
      return;
    }

    // Hard block: free articles must have their unit chosen before submit.
    const missingUnit = lines.filter(
      (l) => l.is_free_article && !(l.item_unit || l.unit)
    );
    if (missingUnit.length > 0) {
      const names = missingUnit.map(l => `“${l.item_name || '—'}”`).join(', ');
      toast.error(`Imposta l'unità di misura per: ${names}`);
      return;
    }

    // Pre-flight: if no signature and not marked as "customer not present",
    // ask the user (desktop ADMIN/SECRETARY rarely has the customer in front of them).
    const hasSignature = !!workOrder.signature_base64;
    const notPresent = !!workOrder.customer_not_present;

    if (!hasSignature && !notPresent) {
      const ok = await confirm({
        title: 'Firma cliente mancante',
        description: "Il bollettino non è stato firmato. Vuoi marcarlo come «Cliente non presente» e inviarlo in revisione? In alternativa, falli firmare prima dall'app mobile.",
        confirmText: 'Marca come «Cliente non presente» e invia',
        cancelText: 'No, torna indietro',
      });
      if (!ok) return;
      try {
        await api.saveWorkOrderSignature(workOrder.id, { customer_not_present: true });
      } catch (e) {
        toast.error(e.response?.data?.detail || 'Errore nel marcare come «Cliente non presente»');
        return;
      }
    }

    try {
      await api.submitWorkOrder(workOrder.id);
      toast.success('Bollettino inviato per revisione');
      loadWorkOrder();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'invio");
    }
  };

  const handleApprove = async () => {
    // Hard block: every free article must have a unit set before approval.
    const missingUnit = lines.filter(
      (l) => l.is_free_article && !(l.item_unit || l.unit)
    );
    if (missingUnit.length > 0) {
      const names = missingUnit.map(l => `“${l.item_name || '—'}”`).join(', ');
      toast.error(`Imposta l'unità di misura per: ${names}`);
      return;
    }
    // Soft warning: free articles missing price/IVA → dialog with "Approva comunque".
    const issues = collectMissingForApproval();
    if (issues.length > 0) {
      setApproveWarnings(issues);
      return;
    }
    await doApprove();
  };

  const doApprove = async () => {
    try {
      // Auto-save header changes before approving if in IN_REVIEW
      if (workOrder.status === 'IN_REVIEW') {
        await saveHeaderChanges();
      }
      await api.approveWorkOrder(workOrder.id);
      toast.success('Bollettino approvato');
      setApproveWarnings(null);
      loadWorkOrder();
    } catch (error) {
      toast.error('Errore nell\'approvazione');
    }
  };

  const handleReject = async () => {
    const comment = window.prompt('Inserisci il motivo del rifiuto (opzionale):');
    if (comment === null) return;

    try {
      // Auto-save header changes before rejecting if in IN_REVIEW
      if (workOrder.status === 'IN_REVIEW') {
        await saveHeaderChanges();
      }
      await api.rejectWorkOrder(workOrder.id, comment || '');
      toast.success('Bollettino rimandato per correzioni');
      loadWorkOrder();
    } catch (error) {
      toast.error('Errore nel rifiuto');
    }
  };

  // Save header fields (customer, site, notes, work_date) silently
  const saveHeaderChanges = async () => {
    if (!workOrder.id) return;
    try {
      const siteIdToSave = workOrder.site_id === '__none__' ? null : workOrder.site_id;
      await api.updateWorkOrder(workOrder.id, {
        customer_id: workOrder.customer_id,
        site_id: siteIdToSave,
        notes: workOrder.notes,
        work_date: workOrder.work_date
      });
      // Refresh audit log so the reviewer sees their changes immediately
      const isReviewing = workOrder.status === 'IN_REVIEW' || workOrder.status === 'NEEDS_CORRECTION';
      if (isReviewing) {
        try {
          const auditResp = await api.getWorkOrderAudit(workOrder.id);
          setAuditEvents(auditResp.data || []);
        } catch {/* non-blocking */}
      }
    } catch (error) {
      console.error('Error saving header changes:', error);
    }
  };

  // Auto-save header on blur for reviewers in IN_REVIEW/NEEDS_CORRECTION.
  // Keeps the audit timeline accurate without forcing the reviewer to hit approve first.
  const handleHeaderBlur = () => {
    const isReviewing = workOrder.status === 'IN_REVIEW' || workOrder.status === 'NEEDS_CORRECTION';
    if (isReviewing && hasRole(['ADMIN', 'MANAGER']) && viewMode !== 'original') {
      saveHeaderChanges();
    }
  };

  const handleReopen = async () => {
    const ok = await confirm({
      title: 'Riapri bollettino',
      description: 'Il bollettino tornerà in stato "In Revisione" e potrà essere modificato nuovamente.',
      confirmText: 'Riapri',
    });
    if (!ok) return;

    try {
      await api.reopenWorkOrder(workOrder.id);
      toast.success('Bollettino riaperto per modifiche');
      loadWorkOrder();
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore nella riapertura';
      toast.error(message);
    }
  };

  const handleCancel = async () => {
    const ok = await confirm({
      title: 'Annulla bollettino',
      description: 'Questa azione marca il bollettino come annullato e non può essere annullata.',
      confirmText: 'Annulla bollettino',
      destructive: true,
    });
    if (!ok) return;

    try {
      await api.cancelWorkOrder(workOrder.id);
      toast.success('Bollettino annullato');
      loadWorkOrder();
    } catch (error) {
      const message = error.response?.data?.detail || 'Errore nell\'annullamento';
      toast.error(message);
    }
  };

  const handleDeleteForever = async () => {
    const ok = await confirm({
      title: `Elimina bollettino ${workOrder?.number || ''}?`,
      description: 'Questa operazione cancella DEFINITIVAMENTE il bollettino e tutte le sue righe. Non è recuperabile.',
      confirmText: 'Elimina definitivamente',
      destructive: true,
    });
    if (!ok) return;
    try {
      await api.deleteWorkOrder(workOrder.id);
      toast.success('Bollettino eliminato');
      navigate('/app/work-orders');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'eliminazione");
    }
  };

  const addLine = async () => {
    // During DRAFT (or new): keep local-state addition (saved on Save)
    // During IN_REVIEW reviewers add lines that persist immediately when item is picked.
    setLines([...lines, {
      item_id: '',
      quantity: 1,
      line_notes: '',
      unit: '',
      standard_price_chf: 0,
      net_amount: 0
    }]);
  };

  const updateLine = (index, field, value) => {
    const newLines = [...lines];
    newLines[index][field] = value;
    
    // If item changed, auto-populate unit and price
    if (field === 'item_id') {
      const item = items.find(i => i.id === value);
      if (item) {
        newLines[index].unit = item.unit;
        if (!isEmployee) {
          // Apply price cascade: Site > Customer > Standard
          const { price } = getPriceForItem(value, items, customerPrices, sitePrices);
          newLines[index].standard_price_chf = item.standard_price_chf;
          newLines[index].assigned_unit_price_chf = price;
          const discount = newLines[index].discount_percent || 0;
          const netBeforeDiscount = price * newLines[index].quantity;
          newLines[index].net_amount = netBeforeDiscount * (1 - discount / 100);
        }
      }

      // ----- Review-mode: persist a brand-new line immediately on first item pick -----
      const isReviewing = workOrder.status === 'IN_REVIEW' || workOrder.status === 'NEEDS_CORRECTION';
      if (!newLines[index].id && isReviewing && hasRole(['ADMIN', 'MANAGER']) && value) {
        (async () => {
          try {
            const resp = await api.addWorkOrderLine(workOrder.id, {
              item_id: value,
              quantity: newLines[index].quantity || 1,
              date: workOrder.work_date || new Date().toISOString().split('T')[0],
              line_notes: newLines[index].line_notes || '',
            });
            // Inject the new id so subsequent edits hit PUT
            setLines(curr => {
              const cp = [...curr];
              if (cp[index]) cp[index] = { ...cp[index], id: resp.data.id, ...resp.data };
              return cp;
            });
            // Refresh audit + totals
            loadWorkOrder();
          } catch (e) {
            toast.error("Errore nell'aggiunta articolo");
          }
        })();
      }
    }
    
    // Recalculate line total locally when qty/discount/price change (no API call here —
    // persistence + audit happen on field blur via commitLineUpdate).
    if ((field === 'quantity' || field === 'discount_percent' || field === 'assigned_unit_price_chf') && !isEmployee) {
      const price = field === 'assigned_unit_price_chf' 
        ? (value ?? 0)
        : (newLines[index].assigned_unit_price_chf || newLines[index].standard_price_chf || 0);
      const quantity = field === 'quantity' ? (value ?? 0) : (newLines[index].quantity ?? 0);
      const discount = field === 'discount_percent' ? (value ?? 0) : (newLines[index].discount_percent || 0);
      const netBeforeDiscount = price * quantity;
      newLines[index].net_amount = netBeforeDiscount * (1 - discount / 100);
    }
    
    setLines(newLines);
  };

  // Commit (persist + audit) a line field change once the user has finished typing.
  // Only fires in IN_REVIEW/NEEDS_CORRECTION for reviewers. Backend already skips
  // audit when the new value equals the old, so repeated blurs cause no spam.
  const commitLineUpdate = (index, field) => {
    const isReviewing = workOrder.status === 'IN_REVIEW' || workOrder.status === 'NEEDS_CORRECTION';
    if (!isReviewing || !hasRole(['ADMIN', 'MANAGER'])) return;
    const line = lines[index];
    if (!line || !line.id) return;
    saveLineUpdate(line.id, field, line[field]);
  };
  
  // Debounced save for line updates
  const saveLineUpdate = async (lineId, field, value) => {
    try {
      const updateData = { [field]: value };
      await api.updateWorkOrderLine(workOrder.id, lineId, updateData);
      // Silently save - no toast for every change. Refresh audit afterwards.
      try {
        const auditResp = await api.getWorkOrderAudit(workOrder.id);
        setAuditEvents(auditResp.data || []);
      } catch {/* non-blocking */}
    } catch (error) {
      console.error('Error saving line update:', error);
    }
  };

  // Request line deletion. During review, show a confirm dialog first.
  const requestRemoveLine = (index) => {
    const line = lines[index];
    const isReviewing = workOrder.status === 'IN_REVIEW' || workOrder.status === 'NEEDS_CORRECTION';
    // In DRAFT or for unsaved local rows, remove immediately (no DB hit)
    if (!line?.id || !isReviewing) {
      setLines(lines.filter((_, i) => i !== index));
      return;
    }
    setDeleteLineIndex(index);
  };

  const removeLine = async () => {
    if (deleteLineIndex == null) return;
    const line = lines[deleteLineIndex];
    try {
      if (line && line.id) {
        await api.deleteWorkOrderLine(workOrder.id, line.id);
      }
      toast.success('Articolo eliminato');
      setDeleteLineIndex(null);
      loadWorkOrder();
    } catch (e) {
      toast.error("Errore nell'eliminazione articolo");
      setDeleteLineIndex(null);
    }
  };

  // Add a free article to the work order. Persists immediately when WO already has an id,
  // otherwise replaces the (empty) target row in local state. Free articles are added
  // with empty unit / IVA — the reviewer fills them inline via the highlighted dropdowns.
  const handleAddFreeArticle = async (name, targetIndex) => {
    const isReviewing = workOrder.status === 'IN_REVIEW' || workOrder.status === 'NEEDS_CORRECTION';
    if (workOrder.id && (isReviewing || workOrder.status === 'DRAFT')) {
      try {
        await api.addWorkOrderLine(workOrder.id, {
          item_id: null,
          item_name: name,
          item_unit: null,
          mwst_rate_snapshot: null,
          is_free_article: true,
          quantity: 1,
          date: workOrder.work_date || new Date().toISOString().split('T')[0],
        });
        // Drop the placeholder empty row immediately (loadWorkOrder will sync the rest)
        if (targetIndex != null) {
          setLines(prev => prev.filter((_, i) => i !== targetIndex));
        }
        toast.success('Articolo libero aggiunto — completa unità e IVA');
        loadWorkOrder();
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Errore nell'aggiunta articolo libero");
      }
      return;
    }
    // No id yet (brand-new WO before first save): REPLACE the placeholder row
    // that the user clicked in (avoiding the "extra empty row" bug).
    setLines(prev => {
      const cp = [...prev];
      const freeRow = {
        item_id: null,
        item_name: name,
        item_unit: '',
        mwst_rate_snapshot: null,
        is_free_article: true,
        quantity: 1,
        line_notes: '',
        unit: '',
      };
      if (targetIndex != null && cp[targetIndex]) {
        cp[targetIndex] = freeRow;
      } else {
        cp.push(freeRow);
      }
      return cp;
    });
  };

  // Promote a free article to a catalog item (reviewer only).
  const handlePromoteToCatalog = async (payload) => {
    if (promoteLineIndex == null) return;
    const line = lines[promoteLineIndex];
    if (!line?.id) { setPromoteLineIndex(null); return; }
    try {
      await api.promoteWorkOrderLineToCatalog(workOrder.id, line.id, payload);
      toast.success("Articolo salvato nel catalogo");
      setPromoteLineIndex(null);
      // Refresh items list + work order
      try { const it = await api.getItems(); setItems(it.data || []); } catch {/* ignore */}
      loadWorkOrder();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel salvataggio nel catalogo");
    }
  };

  // Collect free articles still missing price or MWST to warn before approval.
  const collectMissingForApproval = () => {
    const issues = [];
    for (const ln of lines) {
      const nm = ln.item_name || getItemName(ln.item_id) || '—';
      const reasons = [];
      if (ln.is_free_article) {
        if (ln.assigned_unit_price_chf == null) reasons.push('prezzo');
        if (ln.mwst_rate_snapshot == null) reasons.push('aliquota IVA');
      }
      if (reasons.length) issues.push(`“${nm}” (${reasons.join(', ')})`);
    }
    return issues;
  };

  const getItemName = (itemId) => {
    const item = items.find(i => i.id === itemId);
    return item ? item.name : '';
  };

  const calculateTotals = () => {
    if (isEmployee) return null;
    
    let netTotal = 0;
    const mwstByRate = {}; // Group MWST by rate
    
    lines.forEach(line => {
      let lineNet = 0;
      let mwstRate = 0;
      
      // Get the item to find its MWST rate
      const item = items.find(i => i.id === line.item_id);
      mwstRate = item?.mwst_rate || line.mwst_rate_snapshot || 8.1;
      
      // Use net_amount if available, otherwise calculate from assigned price
      if (line.net_amount) {
        lineNet = line.net_amount;
      } else if ((line.assigned_unit_price_chf || line.standard_price_chf) && line.quantity) {
        const price = line.assigned_unit_price_chf || line.standard_price_chf;
        const discount = line.discount_percent || 0;
        const netBeforeDiscount = price * line.quantity;
        lineNet = netBeforeDiscount * (1 - discount / 100);
      }
      
      netTotal += lineNet;
      
      // Accumulate MWST by rate
      if (!mwstByRate[mwstRate]) {
        mwstByRate[mwstRate] = { net: 0, mwst: 0 };
      }
      mwstByRate[mwstRate].net += lineNet;
      mwstByRate[mwstRate].mwst += lineNet * (mwstRate / 100);
    });
    
    // Calculate total MWST
    let mwstTotal = 0;
    Object.values(mwstByRate).forEach(v => {
      mwstTotal += v.mwst;
    });
    
    return {
      net: netTotal,
      mwst: mwstTotal,
      gross: netTotal + mwstTotal,
      mwstByRate: mwstByRate // Return breakdown by rate
    };
  };

  if (loading) {
    return <div className="p-8">Caricamento...</div>;
  }

  const totals = calculateTotals();
  // ----- Original vs Revised toggle state -----
  const isViewingOriginal = viewMode === 'original' && !!originalSnapshot;
  const canToggleVersion =
    !isNew &&
    !isEmployee &&
    workOrder.has_original_snapshot &&
    ['IN_REVIEW', 'APPROVED', 'INVOICED', 'CANCELLED'].includes(workOrder.status);

  // When in "original" view, render the frozen snapshot. Otherwise, the live data.
  const displayHeader = isViewingOriginal && originalSnapshot ? {
    customer_id: originalSnapshot.customer_id,
    customer_name: originalSnapshot.customer_name,
    site_id: originalSnapshot.site_id,
    site_name: originalSnapshot.site_name,
    site_reference: originalSnapshot.site_reference,
    notes: originalSnapshot.notes,
    work_date: originalSnapshot.work_date,
    signature_base64: originalSnapshot.signature_base64,
    signer_name: originalSnapshot.signer_name,
    customer_not_present: originalSnapshot.customer_not_present,
  } : workOrder;
  const displayLines = isViewingOriginal && originalSnapshot ? (originalSnapshot.lines || []) : lines;
  const displayTotals = isViewingOriginal && originalSnapshot ? {
    net: originalSnapshot.net_total || 0,
    mwst: originalSnapshot.mwst_total || 0,
    gross: originalSnapshot.gross_total || 0,
    mwstByRate: {},
  } : totals;

  // Define canReview first as it's used by canEditAsReviewer
  const canReview = workOrder.status === 'IN_REVIEW' && hasRole(['ADMIN', 'MANAGER']) && !isViewingOriginal;
  // Check if current user is the creator of this work order
  const isCreator = workOrder.created_by === user?.email;
  // Permission to edit based on status and role
  const canEditDraft = workOrder.status === 'DRAFT' && (isCreator || hasRole(['ADMIN']));
  const canEditNeedsCorrection = workOrder.status === 'NEEDS_CORRECTION' && (isCreator || hasRole(['ADMIN', 'MANAGER']));
  const canEditAsReviewer = canReview;
  // User has permission to edit (but may not be in edit mode)
  const hasEditPermission = isNew || canEditDraft || canEditNeedsCorrection || canEditAsReviewer;
  // Actually editable right now (in edit mode and has permission, and not viewing original)
  const canEdit = hasEditPermission && isEditMode && !isViewingOriginal;

  // Header fields logic:
  // - NEW: always editable
  // - DRAFT: editable when isEditMode=true
  // - NEEDS_CORRECTION: editable when isEditMode=true
  // - IN_REVIEW: always unlocked for ADMIN/MANAGER (auto-save on approve/reject)
  // - APPROVED/INVOICED/CANCELLED: locked
  // - When viewing the frozen Original snapshot: always locked.
  const canEditHeaderFields = !isViewingOriginal && (isNew ||
    ((workOrder.status === 'DRAFT' || workOrder.status === 'NEEDS_CORRECTION') && isEditMode) ||
    (workOrder.status === 'IN_REVIEW' && hasRole(['ADMIN', 'MANAGER'])));

  // Can submit to review (only when not in edit mode for DRAFT, to force save first)
  const canSubmit = workOrder.status === 'DRAFT' && !isNew && !isEditMode && lines.length > 0;
  // Show edit button when in view mode and has permission to edit (DRAFT, NEEDS_CORRECTION, or APPROVED)
  // — hidden while viewing the frozen Original snapshot.
  const showEditButton = !isEditMode && !isNew && !isViewingOriginal && (
    (workOrder.status === 'DRAFT' && (isCreator || hasRole(['ADMIN', 'MANAGER']))) ||
    (workOrder.status === 'NEEDS_CORRECTION' && (isCreator || hasRole(['ADMIN', 'MANAGER']))) ||
    (workOrder.status === 'APPROVED' && hasRole(['ADMIN', 'MANAGER']))
  );

  // PDF & Share functions
  const canShareOrPdf = ['IN_REVIEW', 'APPROVED', 'INVOICED'].includes(workOrder.status);

  const handleShowPDF = async () => {
    try {
      const response = await api.downloadWorkOrderPDF(id, viewMode);
      window.open(window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' })), '_blank');
    } catch (error) { toast.error("Errore nell'apertura PDF"); }
  };

  const handleDownloadPDF = async () => {
    try {
      const response = await api.downloadWorkOrderPDF(id, viewMode);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const suffix = viewMode === 'original' ? '-originale' : '';
      const link = document.createElement('a'); link.href = window.URL.createObjectURL(blob); link.download = `${workOrder.number}${suffix}.pdf`; link.click();
      toast.success('PDF scaricato');
    } catch (error) { toast.error('Errore nel download PDF'); }
  };

  const handlePrintPDF = async () => {
    try {
      const response = await api.downloadWorkOrderPDF(id, viewMode);
      const w = window.open(window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' })), '_blank');
      if (w) w.onload = () => w.print();
    } catch (error) { toast.error('Errore nella stampa'); }
  };

  const openShareDialog = async () => {
    // Build recipients list
    const recipients = [];
    const customer = customers.find(c => c.id === workOrder.customer_id);
    if (customer?.billing_email) {
      recipients.push({ id: 'customer', label: `${customer.name_or_company} (cliente)`, email: customer.billing_email, name: customer.name_or_company });
    }
    // Site manager
    if (workOrder.site_id && workOrder.site_id !== '__none__') {
      const site = sites.find(s => s.id === workOrder.site_id);
      if (site?.manager_email) {
        const mgrName = `${site.manager_first_name || ''} ${site.manager_last_name || ''}`.trim();
        recipients.push({ id: 'site_manager', label: `${mgrName} (responsabile progetto)`, email: site.manager_email, name: mgrName });
      }
    }
    // All contacts with email
    try {
      const contactsRes = await api.getCustomerContacts(workOrder.customer_id);
      (contactsRes.data || []).forEach(c => {
        if (c.email) {
          recipients.push({ id: c.id, label: `${c.first_name} ${c.last_name}`, email: c.email, name: `${c.first_name} ${c.last_name}` });
        }
      });
    } catch (e) {
      // Customer contacts fetch is non-critical for the share dialog
      console.warn('[share-dialog] getCustomerContacts failed:', e);
    }

    setShareContacts(recipients);
    // Default: site manager if exists, else customer
    const defaultRecipient = recipients.find(r => r.id === 'site_manager') || recipients[0];
    if (defaultRecipient) {
      setSelectedRecipient(defaultRecipient.id);
      setShareEmail(defaultRecipient.email);
    }
    setShowShareDialog(true);
  };

  const handleShareRecipientChange = (recipientId) => {
    setSelectedRecipient(recipientId);
    const r = shareContacts.find(c => c.id === recipientId);
    if (r) setShareEmail(r.email);
  };

  const handleSendWoEmail = async () => {
    if (!shareEmail) { toast.error('Inserire un indirizzo email'); return; }
    try {
      setSendingWoEmail(true);
      const recipient = shareContacts.find(c => c.id === selectedRecipient);
      await api.sendWorkOrderEmail(id, { email: shareEmail, name: recipient?.name || '' });
      toast.success('Bollettino segnato come inviato');
      setShowShareDialog(false);
      loadWorkOrder();
    } catch (error) { toast.error(error.response?.data?.detail || "Errore nell'invio"); }
    finally { setSendingWoEmail(false); }
  };

  return (
    <div data-testid="work-order-detail-page">
      <div className="mb-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/app/work-orders')}
          className="mb-4"
          data-testid="back-button"
        >
          <ArrowLeft size={18} className="mr-2" />
          Torna ai Bollettini
        </Button>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-4xl font-bold text-slate-900">
              {isNew ? 'Nuovo Bollettino' : workOrder.number}
            </h1>
            {!isNew && (
              <div className="mt-2 flex items-center gap-3">
                <span className={`status-badge ${getStatusColor(workOrder.status)}`}>
                  {getStatusLabel(workOrder.status)}
                </span>
                {workOrder.status === 'APPROVED' && (
                  <span className="text-sm text-green-600 font-medium">
                    Pronto per la fatturazione
                  </span>
                )}
              </div>
            )}
            {!isNew && workOrder.created_by_name && (
              <div className="mt-2 text-sm text-slate-500">
                {workOrder.on_behalf_of_name ? (
                  <>
                    Creato da <span className="font-medium text-slate-700">{workOrder.on_behalf_of_name}</span>
                    <span className="text-amber-700"> (via account condiviso <strong>{workOrder.created_by_name}</strong>)</span>
                  </>
                ) : (
                  <>Creato da <span className="font-medium text-slate-700">{workOrder.created_by_name}</span></>
                )}
                {workOrder.created_at && (
                  <span> il {formatDateTime(workOrder.created_at)}</span>
                )}
              </div>
            )}
          </div>
          <div className="flex gap-2 flex-wrap justify-end">
            {/* PDF & Share buttons - shown when submitted */}
            {canShareOrPdf && (
              <>
                <Button variant="outline" size="sm" onClick={handleShowPDF} data-testid="wo-pdf-btn">
                  <Eye size={16} className="mr-1" /> PDF
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm"><Download size={16} className="mr-1" /> Esporta</Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem onClick={handleDownloadPDF}><Download size={14} className="mr-2" /> Scarica PDF</DropdownMenuItem>
                    <DropdownMenuItem onClick={handlePrintPDF}><Printer size={14} className="mr-2" /> Stampa</DropdownMenuItem>
                    <DropdownMenuItem onClick={openShareDialog}><Mail size={14} className="mr-2" /> Invia via Email</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            )}

            {/* Edit button - shown when viewing a draft/needs_correction/approved that can be edited */}
            {showEditButton && (
              <Button onClick={() => {
                if (workOrder.status === 'APPROVED') {
                  handleReopen();
                } else {
                  setIsEditMode(true);
                }
              }} variant="outline" data-testid="edit-work-order-btn">
                <Edit size={18} className="mr-2" />
                Modifica
              </Button>
            )}
            
            {/* Save button - only in edit mode */}
            {canEdit && (
              <Button onClick={handleSave} disabled={saving} data-testid="save-work-order-btn">
                <Save size={18} className="mr-2" />
                {saving ? 'Salvataggio...' : 'Salva'}
              </Button>
            )}
            
            {/* Submit for review (DRAFT -> IN_REVIEW) - only when not editing */}
            {canSubmit && (
              <Button onClick={handleSubmit} variant="outline" className="text-blue-600 hover:bg-blue-50">
                <Send size={18} className="mr-2" />
                Invia per Revisione
              </Button>
            )}
            
            {/* Resubmit after corrections — hidden during beta:
                reviewers edit directly in IN_REVIEW, no need to bounce back. */}
            
            {/* Review actions (for Manager/Admin) */}
            {canReview && (
              <Button 
                onClick={handleApprove} 
                variant="outline" 
                className="text-green-600 hover:bg-green-50"
                data-testid="approve-btn"
              >
                <Check size={18} className="mr-2" />
                Approva
              </Button>
            )}
            
            {/* Cancel button for work orders in review (for Manager/Admin) */}
            {(workOrder.status === 'IN_REVIEW' || workOrder.status === 'NEEDS_CORRECTION') && hasRole(['ADMIN', 'MANAGER']) && !isViewingOriginal && (
              <Button 
                onClick={handleCancel}
                variant="outline"
                className="text-red-600 hover:bg-red-50"
                data-testid="cancel-btn"
              >
                <X size={18} className="mr-2" />
                Annulla
              </Button>
            )}
            
            {/* Invoice button for approved work orders */}
            {workOrder.status === 'APPROVED' && hasRole(['ADMIN', 'MANAGER', 'SECRETARY']) && (
              <Button 
                onClick={() => navigate(`/app/invoices/generate?customer=${workOrder.customer_id}&workOrder=${id}`)}
                className="bg-green-600 hover:bg-green-700"
                data-testid="invoice-btn"
              >
                <FileText size={18} className="mr-2" />
                Fattura
              </Button>
            )}

            {/* ADMIN-only: hard delete (only for already-cancelled bollettini) */}
            {hasRole(['ADMIN']) && workOrder.status === 'CANCELLED' && (
              <Button
                onClick={handleDeleteForever}
                variant="outline"
                className="text-red-700 border-red-200 hover:bg-red-50"
                data-testid="delete-forever-btn"
              >
                <Trash2 size={18} className="mr-2" />
                Elimina definitivamente
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {/* Originale / Revisione toggle (post-submit) */}
        {canToggleVersion && (
          <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-lg px-4 py-3" data-testid="version-toggle-bar">
            <span className="text-sm font-medium text-slate-700">Visualizza:</span>
            <div className="inline-flex rounded-full bg-white border border-slate-200 p-1 shadow-sm">
              <button
                type="button"
                onClick={() => setViewMode('revised')}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  !isViewingOriginal ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'
                }`}
                data-testid="view-revised-btn"
              >
                Revisione
              </button>
              <button
                type="button"
                onClick={() => setViewMode('original')}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  isViewingOriginal ? 'bg-amber-600 text-white' : 'text-slate-600 hover:bg-slate-50'
                }`}
                data-testid="view-original-btn"
              >
                Originale
              </button>
            </div>
            <span className="text-xs text-slate-500 ml-auto">
              {isViewingOriginal
                ? `Versione operaio congelata${originalSnapshot?.captured_at ? ` il ${formatDateTime(originalSnapshot.captured_at)}` : ''}`
                : 'Versione corrente del bollettino (post-revisione)'}
            </span>
          </div>
        )}

        {/* Header Info */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Informazioni Bollettino</CardTitle>
              <div className="flex items-center gap-2 flex-wrap">
                {workOrder.signature_base64 && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200" data-testid="info-signed-badge">
                    <PenLine className="h-3.5 w-3.5" />
                    Firmato{workOrder.signer_name ? ` da ${workOrder.signer_name}` : ''}
                  </span>
                )}
                {!workOrder.signature_base64 && workOrder.customer_not_present && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200" data-testid="info-not-present-badge">
                    <UserX className="h-3.5 w-3.5" />
                    Cliente non presente
                  </span>
                )}
                {/* Sign / Re-sign button (only for editable bollettini) */}
                {['DRAFT', 'NEEDS_CORRECTION'].includes(workOrder.status) && hasRole(['ADMIN', 'MANAGER', 'SECRETARY', 'EMPLOYEE']) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSignatureDialogOpen(true)}
                    className="h-7 px-2.5 text-xs border-blue-200 text-blue-700 hover:bg-blue-50"
                    data-testid="desktop-sign-btn"
                  >
                    <PenLine className="h-3.5 w-3.5 mr-1.5" />
                    {workOrder.signature_base64 ? 'Modifica firma' : 'Firma cliente'}
                  </Button>
                )}
                {workOrder.email_sent_at && (
                  <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 border border-green-200" data-testid="info-email-badge" title={workOrder.email_sent_to ? `Inviato a ${workOrder.email_sent_to_name || workOrder.email_sent_to}` : 'Email inviata'}>
                    <Mail className="h-3.5 w-3.5" />
                    Copia inviata{workOrder.email_sent_to_name ? ` a ${workOrder.email_sent_to_name}` : workOrder.email_sent_to ? ` a ${workOrder.email_sent_to}` : ''}
                  </span>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Data Bollettino */}
              <div className="space-y-2">
                <Label htmlFor="work-date">Data Bollettino *</Label>
                <Input
                  id="work-date"
                  type="date"
                  value={displayHeader.work_date || ''}
                  onChange={(e) => setWorkOrder({ ...workOrder, work_date: e.target.value })}
                  onBlur={handleHeaderBlur}
                  disabled={!canEditHeaderFields}
                  data-testid="work-date-input"
                />
                <p className="text-xs text-slate-400">Questa data sara usata per tutti gli articoli</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="customer">Cliente *</Label>
                <Select
                  value={displayHeader.customer_id || ''}
                  onValueChange={(value) => {
                    setWorkOrder({ ...workOrder, customer_id: value, site_id: '__none__' });
                    setTimeout(handleHeaderBlur, 0);
                  }}
                  disabled={!canEditHeaderFields}
                >
                  <SelectTrigger id="customer" data-testid="customer-select">
                    <SelectValue placeholder="Seleziona cliente" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map(customer => (
                      <SelectItem key={customer.id} value={customer.id}>
                        {customer.name_or_company}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="site">Progetto</Label>
                <Select
                  value={displayHeader.site_id || '__none__'}
                  onValueChange={(value) => {
                    setWorkOrder({ ...workOrder, site_id: value });
                    setTimeout(handleHeaderBlur, 0);
                  }}
                  disabled={!canEditHeaderFields || !displayHeader.customer_id}
                >
                  <SelectTrigger id="site">
                    <SelectValue placeholder="Seleziona progetto (opzionale)" />
                  </SelectTrigger>
                  <SelectContent>
                    {sites.length === 0 ? (
                      <SelectItem value="none" disabled>Nessun progetto disponibile</SelectItem>
                    ) : (
                      <>
                        <SelectItem value="__none__">Nessun progetto</SelectItem>
                        {sites.map(site => (
                          <SelectItem key={site.id} value={site.id}>
                            {site.site_name} - {site.reference_number}
                          </SelectItem>
                        ))}
                      </>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="mt-4 space-y-2">
              <Label htmlFor="notes">Note Generali</Label>
              <Textarea
                id="notes"
                value={displayHeader.notes || ''}
                onChange={(e) => setWorkOrder({ ...workOrder, notes: e.target.value })}
                onBlur={handleHeaderBlur}
                rows={3}
                disabled={!canEditHeaderFields}
                placeholder="Note del bollettino..."
              />
            </div>
          </CardContent>
        </Card>

        {/* Lines */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Articoli{isViewingOriginal && <span className="ml-2 text-xs font-normal text-amber-700">(versione operaio, sola lettura)</span>}</CardTitle>
            {(canEdit || canEditAsReviewer) && (
              <Button size="sm" onClick={addLine} data-testid="add-line-btn">
                <Plus size={16} className="mr-2" />
                Aggiungi Articolo
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {displayLines.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-slate-600">Nessun articolo aggiunto</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-secondary border-b">
                    <tr>
                      <th className="text-left p-4 text-sm font-semibold text-slate-700">Articolo</th>
                      <th className="text-left p-4 text-sm font-semibold text-slate-700">Note</th>
                      <th className="text-left p-4 text-sm font-semibold text-slate-700">Unita</th>
                      <th className="text-right p-4 text-sm font-semibold text-slate-700">Quantita</th>
                      {!isEmployee && (
                        <>
                          <th className="text-right p-4 text-sm font-semibold text-slate-700">Prezzo Unit.</th>
                          <th className="text-right p-4 text-sm font-semibold text-slate-700">Sconto %</th>
                          <th className="text-right p-4 text-sm font-semibold text-slate-700">Totale</th>
                          <th className="text-right p-4 text-sm font-semibold text-slate-700">IVA %</th>
                        </>
                      )}
                      {(canEdit || canEditAsReviewer) && (
                        <th className="text-center p-4 text-sm font-semibold text-slate-700">Azioni</th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {displayLines.map((line, index) => (
                      <tr key={line.id || `line-tmp-${index}`} className="border-b">
                        <td className="p-4 align-top">
                          {(canEdit || canEditAsReviewer) ? (
                            <div className="flex flex-col gap-1">
                              <ItemCombobox
                                value={line.item_id}
                                freeName={line.is_free_article ? line.item_name : null}
                                items={items}
                                disabled={!canEdit && !canEditAsReviewer}
                                onSelectItem={(it) => updateLine(index, 'item_id', it.id)}
                                onSelectFree={hasRole(['ADMIN', 'MANAGER']) ? ((typed) => handleAddFreeArticle(typed, index)) : undefined}
                                testid={`item-combo-${index}`}
                              />
                              {line.is_free_article && (
                                <div className="flex items-center gap-2 self-start">
                                  <span
                                    className="inline-flex items-center text-[10px] uppercase tracking-wide font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded"
                                    data-testid={`free-article-badge-${index}`}
                                  >
                                    Articolo libero
                                  </span>
                                  {canEditAsReviewer && line.id && (
                                    <button
                                      type="button"
                                      onClick={() => setPromoteLineIndex(index)}
                                      className="text-[10px] uppercase tracking-wide font-semibold text-blue-700 bg-blue-100 hover:bg-blue-200 px-1.5 py-0.5 rounded transition-colors"
                                      data-testid={`promote-line-${index}`}
                                      title="Salva nel catalogo"
                                    >
                                      + Catalogo
                                    </button>
                                  )}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="flex flex-col gap-1">
                              <span>{line.is_free_article ? (line.item_name || '—') : getItemName(line.item_id)}</span>
                              {line.is_free_article && (
                                <span className="inline-flex items-center self-start text-[10px] uppercase tracking-wide font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                                  Articolo libero
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                        <td className="p-4">
                          {(canEdit || canEditAsReviewer) ? (
                            <Input
                              value={line.line_notes || ''}
                              onChange={(e) => updateLine(index, 'line_notes', e.target.value)}
                              onBlur={() => commitLineUpdate(index, 'line_notes')}
                              placeholder="Note articolo..."
                              className="w-48"
                            />
                          ) : (
                            <span className="text-sm text-slate-600">{line.line_notes || '-'}</span>
                          )}
                        </td>
                        <td className="p-4">
                          {(() => {
                            const u = line.item_unit || line.unit || '';
                            // Free articles in review: inline editable dropdown so reviewers
                            // can pick the unit without opening a popup. Highlighted amber
                            // until completed.
                            if (line.is_free_article && (canEdit || canEditAsReviewer)) {
                              const unitOpts = [
                                ...new Set([
                                  ...items.map(it => it.unit).filter(Boolean),
                                  'pz', 'ora', 'kg', 'mq', 'mc', 'km', 'lt', 'forfait',
                                ])
                              ];
                              const missing = !u;
                              return (
                                <select
                                  value={u}
                                  onChange={(e) => {
                                    const v = e.target.value;
                                    if (!v) return;
                                    updateLine(index, 'item_unit', v);
                                    if (line.id) saveLineUpdate(line.id, 'item_unit', v);
                                  }}
                                  className={`h-7 text-sm border rounded px-1 ${missing ? 'border-amber-400 bg-amber-50' : 'border-slate-200'}`}
                                  data-testid={`wo-free-unit-${index}`}
                                >
                                  <option value="" disabled>—</option>
                                  {unitOpts.map(opt => (
                                    <option key={opt} value={opt}>{opt}</option>
                                  ))}
                                </select>
                              );
                            }
                            return <span className="text-sm font-medium text-slate-600">{u || '-'}</span>;
                          })()}
                        </td>
                        <td className="p-4 text-right">
                          {(canEdit || canEditAsReviewer) ? (
                            <NumberInput
                              step="0.5"
                              min="0"
                              value={line.quantity}
                              onChange={(val) => updateLine(index, 'quantity', val)}
                              onBlur={() => commitLineUpdate(index, 'quantity')}
                              className="w-24 text-right"
                            />
                          ) : (
                            <span className="font-mono">{line.quantity}</span>
                          )}
                        </td>
                        {!isEmployee && (
                          <>
                            <td className="p-4 text-right">
                              {canReview || canEditAsReviewer ? (
                                <NumberInput
                                  step="0.01"
                                  min="0"
                                  value={line.assigned_unit_price_chf ?? line.standard_price_chf ?? null}
                                  onChange={(val) => updateLine(index, 'assigned_unit_price_chf', val)}
                                  onBlur={() => commitLineUpdate(index, 'assigned_unit_price_chf')}
                                  className="w-28 text-right font-mono"
                                  placeholder="0.00"
                                />
                              ) : (
                                <span className="font-mono">
                                  {line.assigned_unit_price_chf 
                                    ? formatCurrency(line.assigned_unit_price_chf) 
                                    : line.standard_price_chf 
                                      ? formatCurrency(line.standard_price_chf)
                                      : '-'}
                                </span>
                              )}
                            </td>
                            <td className="p-4 text-right">
                              {canReview || canEditAsReviewer ? (
                                <NumberInput
                                  step="0.5"
                                  min="0"
                                  max="100"
                                  value={line.discount_percent ?? null}
                                  onChange={(val) => updateLine(index, 'discount_percent', val)}
                                  onBlur={() => commitLineUpdate(index, 'discount_percent')}
                                  className="w-20 text-right"
                                  placeholder="0"
                                />
                              ) : (
                                <span className="font-mono">
                                  {line.discount_percent ? `${line.discount_percent}%` : '-'}
                                </span>
                              )}
                            </td>
                            <td className="p-4 text-right font-mono font-medium">
                              {line.net_amount ? formatCurrency(line.net_amount) : '-'}
                            </td>
                            <td className="p-4 text-right text-slate-600">
                              {(() => {
                                const item = items.find(i => i.id === line.item_id);
                                // Free articles: in review mode show a dropdown so the
                                // reviewer can set/change the IVA inline. Otherwise show
                                // "-" if missing so the gap is visible.
                                if (line.is_free_article) {
                                  // Dropdown is shown whenever the line is editable
                                  // (creation OR review). Highlighted amber when empty.
                                  if (canEdit || canEditAsReviewer) {
                                    return (
                                      <select
                                        value={line.mwst_rate_snapshot != null ? String(line.mwst_rate_snapshot) : ''}
                                        onChange={(e) => {
                                          const v = e.target.value;
                                          if (v === '') return;
                                          updateLine(index, 'mwst_rate_snapshot', parseFloat(v));
                                          if (line.id) saveLineUpdate(line.id, 'mwst_rate_snapshot', parseFloat(v));
                                        }}
                                        className={`h-7 text-sm border rounded px-1 text-right ${line.mwst_rate_snapshot == null ? 'border-amber-400 bg-amber-50' : 'border-slate-200'}`}
                                        data-testid={`wo-free-iva-${index}`}
                                      >
                                        <option value="" disabled>—</option>
                                        <option value="8.1">8.1%</option>
                                        <option value="2.6">2.6%</option>
                                        <option value="0">0%</option>
                                      </select>
                                    );
                                  }
                                  return line.mwst_rate_snapshot != null
                                    ? `${line.mwst_rate_snapshot}%`
                                    : '-';
                                }
                                const rate = line.mwst_rate_snapshot ?? item?.mwst_rate;
                                return rate != null ? `${rate}%` : '-';
                              })()}
                            </td>
                          </>
                        )}
                        {(canEdit || canEditAsReviewer) && (
                          <td className="p-4 text-center">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => requestRemoveLine(index)}
                              className="text-destructive hover:bg-destructive/10"
                              data-testid={`remove-line-${index}-btn`}
                            >
                              <Trash2 size={16} />
                            </Button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Totals */}
            {!isEmployee && displayTotals && (displayLines.some(l => l.net_amount || l.standard_price_chf) || workOrder.status !== 'DRAFT') && (
              <div className="mt-6 border-t pt-4">
                <div className="flex justify-end">
                  <div className="w-72 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Netto:</span>
                      <span className="font-mono font-medium">{formatCurrency(displayTotals.net)}</span>
                    </div>
                    {/* Show MWST breakdown by rate */}
                    {isViewingOriginal && originalSnapshot?.mwst_breakdown && originalSnapshot.mwst_breakdown.length > 0 ? (
                      originalSnapshot.mwst_breakdown
                        .filter(b => (b.mwst || b.amount) > 0)
                        .map((breakdown) => (
                          <div key={`mwst-orig-${breakdown.rate}`} className="flex justify-between text-sm">
                            <span className="text-slate-600">IVA ({breakdown.rate}%):</span>
                            <span className="font-mono font-medium">{formatCurrency(breakdown.mwst || breakdown.amount)}</span>
                          </div>
                        ))
                    ) : workOrder.mwst_breakdown && workOrder.mwst_breakdown.length > 0 ? (
                      workOrder.mwst_breakdown
                        .filter(b => (b.mwst || b.amount) > 0)
                        .map((breakdown) => (
                          <div key={`mwst-${breakdown.rate}`} className="flex justify-between text-sm">
                            <span className="text-slate-600">IVA ({breakdown.rate}%):</span>
                            <span className="font-mono font-medium">{formatCurrency(breakdown.mwst || breakdown.amount)}</span>
                          </div>
                        ))
                    ) : displayTotals.mwstByRate && Object.keys(displayTotals.mwstByRate).length > 0 ? (
                      Object.entries(displayTotals.mwstByRate)
                        .filter(([, v]) => v.mwst > 0)
                        .map(([rate, v]) => (
                          <div key={rate} className="flex justify-between text-sm">
                            <span className="text-slate-600">IVA ({rate}%):</span>
                            <span className="font-mono font-medium">{formatCurrency(v.mwst)}</span>
                          </div>
                        ))
                    ) : displayTotals.mwst > 0 ? (
                      <div className="flex justify-between text-sm">
                        <span className="text-slate-600">IVA:</span>
                        <span className="font-mono font-medium">{formatCurrency(displayTotals.mwst)}</span>
                      </div>
                    ) : null}
                    <div className="flex justify-between border-t pt-2">
                      <span className="font-semibold">Totale:</span>
                      <span className="font-mono font-bold text-lg">{formatCurrency(displayTotals.gross)}</span>
                    </div>
                    {workOrder.status === 'DRAFT' && (
                      <p className="text-xs text-slate-500 mt-2">
                        * Totale indicativo. I prezzi definitivi verranno assegnati durante la revisione.
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Workflow Timeline */}
        {!isNew && workOrder.status !== 'DRAFT' && (
          <Card className="mt-4">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock size={20} />
                Cronologia
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {/* Created */}
                <div className="flex items-start gap-3">
                  <div className="w-2 h-2 mt-2 rounded-full bg-slate-400"></div>
                  <div>
                    <p className="text-sm font-medium">Creato</p>
                    <p className="text-xs text-slate-500">
                      {formatDateTime(workOrder.created_at)} da {workOrder.created_by_name || workOrder.created_by}
                    </p>
                  </div>
                </div>
                
                {/* Submitted */}
                {workOrder.submitted_at && (
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 mt-2 rounded-full bg-blue-400"></div>
                    <div>
                      <p className="text-sm font-medium">Inviato per revisione</p>
                      <p className="text-xs text-slate-500">{formatDateTime(workOrder.submitted_at)}</p>
                    </div>
                  </div>
                )}
                
                {/* Prices Assigned */}
                {workOrder.pricing_assigned_at && (
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 mt-2 rounded-full bg-purple-400"></div>
                    <div>
                      <p className="text-sm font-medium">Prezzi assegnati</p>
                      <p className="text-xs text-slate-500">{formatDateTime(workOrder.pricing_assigned_at)}</p>
                    </div>
                  </div>
                )}
                
                {/* Approved */}
                {workOrder.approved_at && (
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 mt-2 rounded-full bg-green-500"></div>
                    <div>
                      <p className="text-sm font-medium text-green-700">Approvato</p>
                      <p className="text-xs text-slate-500">
                        {formatDateTime(workOrder.approved_at)} da {workOrder.approved_by_name || workOrder.approved_by}
                      </p>
                    </div>
                  </div>
                )}
                
                {/* Reopened */}
                {workOrder.reopened_at && (
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 mt-2 rounded-full bg-amber-500"></div>
                    <div>
                      <p className="text-sm font-medium text-amber-700">Riaperto per modifiche</p>
                      <p className="text-xs text-slate-500">
                        {formatDateTime(workOrder.reopened_at)} da {workOrder.reopened_by_name || workOrder.reopened_by}
                      </p>
                    </div>
                  </div>
                )}
                
                {/* Rejected - show always if rejected_at exists (for history) */}
                {workOrder.rejected_at && (
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 mt-2 rounded-full bg-orange-500"></div>
                    <div>
                      <p className="text-sm font-medium text-orange-700">Rimandato per correzioni</p>
                      <p className="text-xs text-slate-500">
                        {formatDateTime(workOrder.rejected_at)} da {workOrder.rejected_by_name || workOrder.rejected_by}
                      </p>
                    </div>
                  </div>
                )}
                
                {/* Invoiced - show always if invoiced_at exists (even if invoice was cancelled later) */}
                {workOrder.invoiced_at && (
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 mt-2 rounded-full bg-indigo-500"></div>
                    <div>
                      <p className="text-sm font-medium text-indigo-700">Fatturato</p>
                      <p className="text-xs text-slate-500">
                        {formatDateTime(workOrder.invoiced_at)}
                        {(workOrder.invoiced_by_name || workOrder.invoiced_by) && 
                          <> da {workOrder.invoiced_by_name || workOrder.invoiced_by}</>
                        }
                      </p>
                    </div>
                  </div>
                )}
                
                {/* Invoice Cancelled - show when invoice was cancelled and work order reopened */}
                {workOrder.invoice_cancelled_at && (
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 mt-2 rounded-full bg-red-400"></div>
                    <div>
                      <p className="text-sm font-medium text-red-700">Fattura annullata</p>
                      <p className="text-xs text-slate-500">
                        {formatDateTime(workOrder.invoice_cancelled_at)}
                        {(workOrder.invoice_cancelled_by_name || workOrder.invoice_cancelled_by) && 
                          <> da {workOrder.invoice_cancelled_by_name || workOrder.invoice_cancelled_by}</>
                        }
                      </p>
                    </div>
                  </div>
                )}
                
                {/* Cancelled - show when work order is cancelled */}
                {workOrder.status === 'CANCELLED' && workOrder.cancelled_at && (
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 mt-2 rounded-full bg-slate-500"></div>
                    <div>
                      <p className="text-sm font-medium text-slate-700">Annullato</p>
                      <p className="text-xs text-slate-500">
                        {formatDateTime(workOrder.cancelled_at)}
                        {(workOrder.cancelled_by_name || workOrder.cancelled_by) && 
                          <> da {workOrder.cancelled_by_name || workOrder.cancelled_by}</>
                        }
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* ---- Audit log: modifiche di dettaglio durante la revisione ---- */}
              {/* Nascosto agli EMPLOYEE: non devono vedere le modifiche apportate dal revisore */}
              {!isEmployee && auditEvents.filter(e => ['HEADER_UPDATED', 'NOTES_UPDATED', 'LINE_ADDED', 'LINE_REMOVED', 'LINE_UPDATED'].includes(e.action)).length > 0 && (
                <div className="mt-6 pt-4 border-t border-slate-200">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
                    Modifiche durante la revisione
                  </p>
                  <div className="space-y-2.5">
                    {auditEvents
                      .filter(e => ['HEADER_UPDATED', 'NOTES_UPDATED', 'LINE_ADDED', 'LINE_REMOVED', 'LINE_UPDATED'].includes(e.action))
                      .map(e => {
                        const dot = {
                          LINE_ADDED: 'bg-emerald-500',
                          LINE_REMOVED: 'bg-rose-500',
                          LINE_UPDATED: 'bg-sky-500',
                          HEADER_UPDATED: 'bg-violet-500',
                          NOTES_UPDATED: 'bg-violet-500',
                        }[e.action] || 'bg-slate-400';
                        return (
                          <div key={e.id} className="flex items-start gap-3" data-testid={`audit-event-${e.action.toLowerCase()}`}>
                            <div className={`w-2 h-2 mt-2 rounded-full ${dot}`}></div>
                            <div className="flex-1">
                              <p className="text-sm text-slate-800">{e.description}</p>
                              <p className="text-xs text-slate-500">{formatDateTime(e.created_at)}</p>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Rejection Comment - Separate from timeline for better visibility */}
        {workOrder.status === 'NEEDS_CORRECTION' && workOrder.rejection_comment && (
          <Card className="mt-4 border-orange-200 bg-orange-50">
            <CardHeader>
              <CardTitle className="text-orange-900 flex items-center gap-2">
                <AlertTriangle size={20} />
                Motivo Rimando
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-orange-800">{workOrder.rejection_comment}</p>
              <p className="text-sm text-orange-600 mt-2">
                Rimandato da: {workOrder.rejected_by_name || workOrder.rejected_by} il {new Date(workOrder.rejected_at).toLocaleDateString('it-IT')}
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Share Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent data-testid="share-wo-dialog">
          <DialogHeader><DialogTitle>Condividi Bollettino {workOrder.number}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Destinatario</Label>
              <Select value={selectedRecipient} onValueChange={handleShareRecipientChange}>
                <SelectTrigger><SelectValue placeholder="Seleziona destinatario..." /></SelectTrigger>
                <SelectContent>
                  {shareContacts.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={shareEmail} onChange={(e) => setShareEmail(e.target.value)} placeholder="email@esempio.ch" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={handleShowPDF}><Eye size={14} className="mr-1" /> Anteprima PDF</Button>
            <Button onClick={handleSendWoEmail} disabled={sendingWoEmail}>
              <Mail size={16} className="mr-1" /> {sendingWoEmail ? 'Invio...' : 'Invia via Email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Desktop signature dialog */}
      <SignatureDialog
        open={signatureDialogOpen}
        onOpenChange={setSignatureDialogOpen}
        onConfirm={handleSaveSignature}
        initialSignerName={workOrder.signer_name || workOrder.customer_name || ''}
      />

      {/* Confirm dialog: delete a line during review */}
      <AlertDialog open={deleteLineIndex !== null} onOpenChange={(open) => { if (!open) setDeleteLineIndex(null); }}>
        <AlertDialogContent data-testid="delete-line-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare questo articolo?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteLineIndex !== null && lines[deleteLineIndex] ? (
                <>L&apos;articolo <strong>“{lines[deleteLineIndex].is_free_article ? (lines[deleteLineIndex].item_name || '—') : getItemName(lines[deleteLineIndex].item_id)}”</strong> verrà rimosso dal bollettino. L&apos;azione resterà tracciata nella cronologia.</>
              ) : (
                <>L&apos;articolo verrà rimosso dal bollettino.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-line-cancel-btn">Annulla</AlertDialogCancel>
            <AlertDialogAction
              onClick={removeLine}
              className="bg-destructive text-white hover:bg-destructive/90"
              data-testid="delete-line-confirm-btn"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Free article dialog (unit + IVA) */}
      <FreeArticleDialog
        open={freeDialogOpen}
        onOpenChange={(open) => { if (!open) { setFreeDialogOpen(false); setFreeDialogTargetIndex(null); } }}
        initialName={freeDialogInitialName}
        existingUnits={[...new Set(items.map(it => it.unit).filter(Boolean))]}
        onConfirm={handleAddFreeArticle}
      />

      {/* Promote-to-catalog dialog (reviewer only) */}
      <PromoteToCatalogDialog
        open={promoteLineIndex !== null}
        onOpenChange={(open) => { if (!open) setPromoteLineIndex(null); }}
        line={promoteLineIndex !== null ? lines[promoteLineIndex] : null}
        onConfirm={handlePromoteToCatalog}
      />

      {/* Approve warning dialog: free articles missing price/IVA */}
      <AlertDialog open={approveWarnings !== null} onOpenChange={(open) => { if (!open) setApproveWarnings(null); }}>
        <AlertDialogContent data-testid="approve-warning-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Articoli liberi incompleti</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>I seguenti articoli liberi non hanno ancora prezzo o aliquota IVA:</p>
                <ul className="list-disc list-inside text-sm text-slate-700">
                  {(approveWarnings || []).map((m, i) => <li key={i}>{m}</li>)}
                </ul>
                <p className="text-sm">
                  Puoi approvare comunque; i dati mancanti saranno richiesti
                  prima della fatturazione.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="approve-warning-cancel">Torna indietro</AlertDialogCancel>
            <AlertDialogAction onClick={doApprove} data-testid="approve-warning-confirm">
              Approva comunque
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
